import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState, type RefObject } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/auth'
import { registerApi, registerEmployeeApi } from '@/lib/api'
import { BRAND_MINT, formatCents, flagEmoji } from '@/lib/utils'
import { useDetectedCountry } from '@/hooks/useDetectedCountry'
import { toast } from 'sonner'
import {
  ArrowRightIcon,
  EyeIcon,
  EyeOffIcon,
  CheckCircleIcon,
  BuildingIcon,
  TrendingUpIcon,
  ShieldCheckIcon,
} from 'lucide-react'

/**
 * The hero cycles through three thesis statements every 6 seconds. Each is a
 * lived-experience statement about cross-border payroll (the subject pinned in
 * DESIGN.md §0), not a metric panel — the skill explicitly warns against the
 * "big number + small label + stats row + gradient accent" AI-template hero.
 */
const THESES: readonly string[] = [
  'Three employees.',
  'One circle.',
  '$2,500 for whoever needs it first.',
]

/**
 * Returns true if the user has asked the OS to dampen motion. We honour that
 * by skipping the rotating thesis altogether (static first thesis is shown
 * instead), and by removing the conditional reveal fade on the company-name
 * field via `motion-safe:` Tailwind utilities.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

// ─────────────────────────────────────────────────────────────
// Scroll-reveal hook
// ─────────────────────────────────────────────────────────────
function useInView(ref: RefObject<HTMLElement | null>, opts?: IntersectionObserverInit) {
  const [inView, setInView] = useState(false)
  const threshold = opts?.threshold ?? 0.1
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.unobserve(el)
        }
      },
      { threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, threshold])
  return inView
}

function RevealSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref)
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out will-change-transform ${
        inView ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      } ${className}`}
    >
      {children}
    </div>
  )
}

export const Route = createFileRoute('/signup')({
  component: () => {
    const navigate = useNavigate()
    const { login } = useAuth()
    const [isCompany, setIsCompany] = useState(false)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [preferredTier, setPreferredTier] = useState<'growth' | 'enterprise'>('growth')
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')
    const [loading, setLoading] = useState(false)
    const [thesisIndex, setThesisIndex] = useState(0)
    const [showPassword, setShowPassword] = useState(false)

    /** Country auto-detected on the pricing page — reused here so the tier cards
     *  show the real price for the visitor's jurisdiction and the detected
     *  country_id flows into registration → the org's funding circle profile. */
    const { country, plan, enterprise_plan: enterprisePlan } = useDetectedCountry()

    // Detected (or EUR-default) prices for the tier cards — same derivation as
    // the pricing page: monthly from the detected plan, annual at the quoted
    // discount (Growth 5/7 ≈ 29% off, Enterprise 20% off).
    const growthMonthlyCents = plan?.price_per_user_cents ?? 700
    const growthAnnualCents = Math.round(growthMonthlyCents * (5 / 7))
    const enterpriseMonthlyCents = enterprisePlan?.price_per_user_cents ?? 1500
    const enterpriseAnnualCents = Math.round(enterpriseMonthlyCents * 0.8)
    const currencySymbol = plan?.currency_symbol ?? '€'

    /**
     * 6-second rotation through the three thesis statements. Returns early
     * when prefers-reduced-motion is set — the static first thesis is the
     * fallback, no animation, no interval.
     */
    useEffect(() => {
      if (prefersReducedMotion()) return
      const intervalId = setInterval(() => {
        setThesisIndex((i) => (i + 1) % THESES.length)
      }, 6000)
      return () => clearInterval(intervalId)
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (!firstName || !lastName || !email || !password) {
        toast.error('Please fill in all required fields')
        return
      }

      setLoading(true)
      try {
        const data = isCompany
          ? await registerApi({
              first_name: firstName,
              last_name: lastName,
              email,
              password,
              role: 'company_admin',
              org_name: companyName,
              preferred_tier: preferredTier,
              billing_cycle: billingCycle,
              country_id: country?.country_id,
            })
          : await registerEmployeeApi({
              first_name: firstName,
              last_name: lastName,
              email,
              password,
              role: 'employee',
            })

        // Search for the JWT token across common nesting patterns.
        let token: string | undefined
        const sources: Record<string, unknown>[] = [data as Record<string, unknown>]
        const dataObj = data.data as Record<string, unknown> | undefined
        if (dataObj) sources.push(dataObj)
        for (const src of sources) {
          const val = src.token
          if (typeof val === 'string' && val.length > 0) {
            token = val
            break
          }
        }

        if (!token) {
          throw new Error('No token received from server')
        }

        await login(token, data)

        toast.success('Welcome to PeerPass! Let\'s set up your team.')
        navigate({ to: '/dashboard' })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="relative flex min-h-svh flex-col bg-[#0B1120] overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Decorative glow orbs */}
        <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />
        <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[300px] rounded-full bg-[#00E5A0]/5 blur-[80px]" />

        {/* Brand lockup */}
        <div className="relative z-10 flex items-center justify-center pt-24 sm:pt-28">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold shadow-lg shadow-[#00E5A0]/30">
              PP
            </div>
            <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
          </Link>
        </div>

        {/* Signup form */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-12 sm:pb-16 pt-6">
          <RevealSection className="w-full max-w-lg">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/50">
              {/* Mint accent bar */}
              <span
                aria-hidden="true"
                className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
                style={{ backgroundColor: BRAND_MINT }}
              />

              {/* Hero — rotating serif thesis + mint dot + cost-of-action subtitle.
                  All three thesis texts are stacked in a CSS Grid cell so the
                  container auto-sizes to the tallest one, keeping the form below
                  from shifting as the text rotates every 6s. */}
              <header className="flex flex-col gap-3 text-center mb-8">
                <div className="flex items-baseline justify-center gap-2">
                  <span
                    className="inline-block shrink-0 rounded-full self-center"
                    style={{ width: '6px', height: '6px', backgroundColor: BRAND_MINT }}
                    aria-hidden="true"
                  />
                  <div className="grid">
                    {THESES.map((thesis, i) => (
                      <h1
                        key={i}
                        aria-hidden={i !== thesisIndex}
                        className={`col-start-1 row-start-1 font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl font-medium tracking-tight text-white md:text-4xl ${
                          i === thesisIndex ? 'visible' : 'invisible'
                        }`}
                      >
                        {thesis}
                      </h1>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-white/60 leading-relaxed">
                  Create your employer account.
                  <br />
                  Takes ninety seconds.
                </p>
              </header>

              {/* ── Progress indicator (Goal Gradient — never starts at 0%) ── */}
              {(() => {
                const filled = [firstName, lastName, email, password, !isCompany || companyName].filter(Boolean).length
                const total = 5
                const pct = Math.round((filled / total) * 100)
                return (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-medium uppercase tracking-widest text-white/30">
                        {pct < 100 ? 'Setting up' : 'Ready to go'}
                      </span>
                      <span className="text-[10px] font-semibold text-[#00E5A0]">{pct}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#00E5A0] transition-all duration-300 ease-out"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )
              })()}

              {/* ── Reciprocity statement (value before the ask) ── */}
              <p className="text-xs text-center text-white/40 mb-6 leading-relaxed">
                Your team is losing{' '}
                <span className="font-semibold text-white/60">$1,750/employee/year</span>{' '}
                to financial stress. PeerPass fixes that in 90 seconds.
              </p>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
                aria-label="Create an account"
              >
                {/* Name fields */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="firstName" className="text-sm font-medium text-white/70">
                      First name
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="Jane"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoFocus
                      required
                      className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="lastName" className="text-sm font-medium text-white/70">
                      Last name
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="text-sm font-medium text-white/70">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
                  />
                </div>

                {/* Password */}
                <div className="flex flex-col gap-2">
                  <Label htmlFor="password" className="text-sm font-medium text-white/70">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                    </button>
                  </div>
                </div>

                {/* Company checkbox */}
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-3 py-3 transition-colors hover:bg-white/[0.06] has-[:focus-visible]:border-[#00E5A0] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#00E5A0]/30">
                  <Checkbox
                    checked={isCompany}
                    onCheckedChange={(checked) => setIsCompany(Boolean(checked))}
                    className="border-white/30 data-[state=checked]:bg-[#00E5A0] data-[state=checked]:text-[#0B1120]"
                  />
                  <span className="flex items-center gap-2 text-sm text-white/70">
                    <BuildingIcon className="size-4 text-white/40" />
                    I'm setting this up for my company
                  </span>
                </label>

                {/* Company name (conditional reveal) */}
                {isCompany && (
                  <div className="flex flex-col gap-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200">
                    <Label htmlFor="company" className="text-sm font-medium text-white/70">
                      Company name
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      placeholder="Acme Inc."
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
                    />
                  </div>
                )}

                {/* Tier selection (conditional reveal) */}
                {isCompany && (
                  <div className="flex flex-col gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-200">
                    <Label className="text-sm font-medium text-white/70">
                      Choose your plan
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Growth card */}
                      <button
                        type="button"
                        onClick={() => setPreferredTier('growth')}
                        className={`relative flex flex-col gap-2 rounded-xl border p-4 text-left transition-all ${
                          preferredTier === 'growth'
                            ? 'border-[#00E5A0] bg-[#00E5A0]/[0.06] ring-1 ring-[#00E5A0]/30'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                        }`}
                      >
                        {preferredTier === 'growth' && (
                          <span className="absolute -top-2 right-3 rounded-full bg-[#00E5A0] px-2 py-0.5 text-[10px] font-bold text-[#0B1120] uppercase tracking-wider">
                            Selected
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          <div className={`flex size-7 items-center justify-center rounded-lg ${preferredTier === 'growth' ? 'bg-[#00E5A0]/10' : 'bg-white/5'}`}>
                            <TrendingUpIcon className={`size-4 ${preferredTier === 'growth' ? 'text-[#00E5A0]' : 'text-white/30'}`} />
                          </div>
                          <span className="text-sm font-semibold text-white">Growth</span>
                        </div>
                        <p className="text-xs text-white/50">Bulk import · Tax forms · Growth tools</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-[#00E5A0]">
                            {currencySymbol}
                            {formatCents(billingCycle === 'annual' ? growthAnnualCents : growthMonthlyCents)}
                            /user
                          </p>
                          <span className="rounded-full bg-[#00E5A0]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#00E5A0]">
                            vs $1,750 lost to stress
                          </span>
                        </div>
                      </button>

                      {/* Enterprise card */}
                      <button
                        type="button"
                        onClick={() => setPreferredTier('enterprise')}
                        className={`relative flex flex-col gap-2 rounded-xl border p-4 text-left transition-all ${
                          preferredTier === 'enterprise'
                            ? 'border-[#00E5A0] bg-[#00E5A0]/[0.06] ring-1 ring-[#00E5A0]/30'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                        }`}
                      >
                        {preferredTier === 'enterprise' && (
                          <span className="absolute -top-2 right-3 rounded-full bg-[#00E5A0] px-2 py-0.5 text-[10px] font-bold text-[#0B1120] uppercase tracking-wider">
                            Selected
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          <div className={`flex size-7 items-center justify-center rounded-lg ${preferredTier === 'enterprise' ? 'bg-[#00E5A0]/10' : 'bg-white/5'}`}>
                            <ShieldCheckIcon className={`size-4 ${preferredTier === 'enterprise' ? 'text-[#00E5A0]' : 'text-white/30'}`} />
                          </div>
                          <span className="text-sm font-semibold text-white">Enterprise</span>
                        </div>
                        <p className="text-xs text-white/50">Growth features + SSO · API · SLA support</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-[#00E5A0]">
                            {currencySymbol}
                            {formatCents(billingCycle === 'annual' ? enterpriseAnnualCents : enterpriseMonthlyCents)}
                            /user
                          </p>
                          <span className="rounded-full bg-[#00E5A0]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#00E5A0]">
                            vs $1,750 lost to stress
                          </span>
                        </div>
                      </button>
                    </div>
                    {/* Monthly / Annual billing toggle */}
                    <div className="flex flex-col gap-2 pt-1">
                      <div className="inline-flex items-center gap-1 self-start rounded-lg border border-white/10 bg-white/[0.04] p-0.5">
                        <button
                          type="button"
                          onClick={() => setBillingCycle('monthly')}
                          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                            billingCycle === 'monthly'
                              ? 'bg-[#00E5A0] text-[#0B1120]'
                              : 'text-white/50 hover:text-white/80'
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          onClick={() => setBillingCycle('annual')}
                          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1.5 ${
                            billingCycle === 'annual'
                              ? 'bg-[#00E5A0] text-[#0B1120]'
                              : 'text-white/50 hover:text-white/80'
                          }`}
                        >
                          Annual
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                            billingCycle === 'annual'
                              ? 'bg-[#0B1120]/15 text-[#0B1120]'
                              : 'bg-[#00E5A0]/20 text-[#00E5A0]'
                          }`}>
                            Save {preferredTier === 'growth' ? '29%' : '20%'}
                          </span>
                        </button>
                      </div>
                    </div>
                    {country && (
                      <p className="text-[10px] text-white/40">
                        {country.flag_url ? (
                          <img src={country.flag_url} alt="" aria-hidden="true" className="mr-0.5 inline-block h-3 w-4 rounded-[2px] object-cover align-[-1px]" />
                        ) : (
                          <span aria-hidden="true">{flagEmoji(country.iso_2)}</span>
                        )}{' '}
                        Prices shown for {country.country}
                        {country.pricing_band ? ` · Band ${country.pricing_band}` : ''} ·{' '}
                        {plan?.currency ?? 'EUR'}
                      </p>
                    )}
                    <p className="text-[10px] text-white/30">
                      You can upgrade or downgrade anytime. 90-day free trial included
                      {billingCycle === 'annual' ? ' — billed annually after trial' : ''}.
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 mt-2 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                      Setting up your account…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Create account
                      <ArrowRightIcon className="size-4" />
                    </span>
                  )}
                </Button>
              </form>

              {/* Footer link */}
              <p className="mt-6 text-center text-sm text-white/40">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 underline underline-offset-4 transition-colors"
                >
                  Log in
                </Link>
              </p>
            </div>
          </RevealSection>
        </div>

        {/* Bottom trust bar */}
        <div className="relative z-10 flex justify-center pb-8">
          <div className="flex items-center gap-4 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              Employer-paid benefit
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              0% interest
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              100% anonymous
            </span>
          </div>
        </div>
      </div>
    )
  },
})
